import {
  isWorkflowRunTerminal,
  localizedWorkflowStepTitle,
  WorkflowRun,
  WorkflowRunStep,
  WorkflowRunStepState,
} from '@digital-twins/shared-types'

import {
  getThreadPlaceholders,
  resolveThreadLanguage,
} from '@/lib/constants/languages'

export type ThreadRunActivityPhase =
  | 'starting'
  | 'preparing'
  | 'sampling'
  | 'writing'
  | 'posting'

export interface ThreadRunActivityLabel {
  phase: ThreadRunActivityPhase
  labelKey:
    | 'runStarting'
    | 'runPreparing'
    | 'runSampling'
    | 'runWriting'
    | 'runPosting'
  /**
   * Prompt and report busy lines show the live step title (workflow overlay,
   * thread language) instead of the /test leftover "Preparing your questions".
   */
  usesStepTitle: boolean
  progress: {
    completed: number | null
    total: number | null
    questionIndex?: number | null
  } | null
}

/** A watched `:step` in one of these states should refresh living cards and outcome lines. */
export function isSettledStepRefreshState(
  state: WorkflowRunStepState,
): boolean {
  return state === 'completed' || state === 'failed'
}

/**
 * Stable key of completed/failed steps. The thread page effect depends on this
 * so a `:step` merge (status still `running`) still refreshes artifacts and
 * messages. Mid-run opens include earlier settled steps; the running step does
 * not appear.
 */
export function settledStepRefreshSignature(
  run: WorkflowRun | null,
): string {
  if (run === null) {
    return ''
  }
  return run.steps
    .filter((step) => isSettledStepRefreshState(step.state))
    .map((step) => `${step.stepId}:${step.state}`)
    .join('|')
}

function withBusyEllipsis(title: string): string {
  if (title.endsWith('\u2026') || title.endsWith('...')) {
    return title
  }
  return `${title}\u2026`
}

/** Busy states shared by prompt, report and slack_webhook steps. */
function isBusyStepState(state: WorkflowRunStepState): boolean {
  return state === 'queued' || state === 'running'
}

/**
 * The step the busy line should describe. Normally `steps[currentStepIndex]`.
 * Right after a gate is approved the server answers with the gate still as
 * the current step (now `completed`) and the next step `queued`; the advancer
 * only moves `currentStepIndex` when its job claims the step. On a
 * non-terminal run a completed current step therefore resolves to the step
 * after it, so the line names what is about to run instead of going blank
 * under the "Approved by" line.
 */
export function resolveThreadRunActiveStep(
  run: WorkflowRun,
): WorkflowRunStep | null {
  const current = run.steps[run.currentStepIndex]
  if (!current) {
    return null
  }
  if (current.state === 'completed' && !isWorkflowRunTerminal(run.status)) {
    return run.steps[run.currentStepIndex + 1] ?? current
  }
  return current
}

/**
 * Copy for the in-thread spinner. Step titles follow the workflow overlay
 * (thread language), not UI i18n. Sampling / starting / posting stay on the
 * generic placeholders — those phases are not a named prompt.
 */
export function formatThreadRunActivityLabel(
  activity: ThreadRunActivityLabel,
  run: WorkflowRun | null,
  language: string | null | undefined,
): string {
  if (activity.usesStepTitle && run) {
    const step = resolveThreadRunActiveStep(run)
    if (step) {
      const title = localizedWorkflowStepTitle({
        templateSlug: run.templateSlug,
        stepKey: step.stepKey,
        language,
        fallback: step.title,
      }).trim()
      if (title.length > 0) {
        return withBusyEllipsis(title)
      }
    }
  }

  return getThreadPlaceholders(resolveThreadLanguage(language))[activity.labelKey]
}

/**
 * Pure busy-phase label for in-thread workflow run activity.
 * Returns null when the gate owns the surface, the run is terminal, or the
 * active step is not a preparing/sampling busy state. The active step is
 * `resolveThreadRunActiveStep`, not the raw `currentStepIndex`.
 */
export function deriveThreadRunActivityLabel(
  run: WorkflowRun | null,
  options: { isStartingRun?: boolean },
): ThreadRunActivityLabel | null {
  if (run === null) {
    if (options.isStartingRun) {
      return {
        phase: 'starting',
        labelKey: 'runStarting',
        usesStepTitle: false,
        progress: null,
      }
    }
    return null
  }

  if (isWorkflowRunTerminal(run.status)) {
    return null
  }

  const step = resolveThreadRunActiveStep(run)
  if (!step) {
    return null
  }

  if (step.state === 'awaiting_approval') {
    return null
  }

  /* `queued` counts as busy for these kinds: the run is live and the step is
     about to be claimed, most visibly right after a gate approval. quick_study
     stays running / waiting_external only, so sampling polling is unaffected. */
  if (step.stepKind === 'prompt' && isBusyStepState(step.state)) {
    return {
      phase: 'preparing',
      labelKey: 'runPreparing',
      usesStepTitle: true,
      progress: null,
    }
  }

  if (step.stepKind === 'report' && isBusyStepState(step.state)) {
    return {
      phase: 'writing',
      labelKey: 'runWriting',
      usesStepTitle: true,
      progress: null,
    }
  }

  if (step.stepKind === 'slack_webhook' && isBusyStepState(step.state)) {
    return {
      phase: 'posting',
      labelKey: 'runPosting',
      usesStepTitle: false,
      progress: null,
    }
  }

  if (
    step.stepKind === 'quick_study' &&
    (step.state === 'running' || step.state === 'waiting_external')
  ) {
    return {
      phase: 'sampling',
      labelKey: 'runSampling',
      usesStepTitle: false,
      progress: step.progress ?? null,
    }
  }

  return null
}

/**
 * True only while a `quick_study` step is sampling.
 */
export function shouldPollWorkflowRunReadback(
  run: WorkflowRun | null,
): boolean {
  return deriveThreadRunActivityLabel(run, {})?.phase === 'sampling'
}
